import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from "rxjs/operators";
import {AuthService} from "./auth.service";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu Zamówień Sklepu Partnerskiego.
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * do przeglądania i obsługi zamówień e-commerce realizowanych we współpracy z partnerami.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerShopOrdersAccessGuard implements CanActivate, CanLoad {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Weryfikuje uprawnienia przed fizyczną aktywacją i wyrenderowaniem widoku
   * listy zamówień partnerskich. W przypadku braku autoryzacji sesji (HTTP 401)
   * wymusza proces bezpiecznego wylogowania. W przypadku braku uprawnień domenowych,
   * deleguje wyświetlenie komunikatu (Toast) do klasy narzędziowej CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partner-shop-orders-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - partner-shop-orders - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) paczki JavaScript
   * modułu zamówień partnerskich z serwera. Chroni kod źródłowy panelu przed
   * transferem do przeglądarki nieuprawnionego użytkownika.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partner-shop-orders-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }), map(response => {
        console.log('check - partner-shop-orders - ok');
        return true;
      }));
  }
}
